import "react-loading-skeleton/dist/skeleton.css";

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable
} from "@tanstack/react-table";
import moment from "moment";
import React, { useEffect, useState } from "react";
import {
  Button,
  Card as CardType,
  Col,
  Container,
  Dropdown,
  Modal,
  Row,
  Spinner,
  Table
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { AiOutlineCheck, AiOutlineClose } from "react-icons/ai";
import { BiTrash } from "react-icons/bi";
import { BsPlusLg } from "react-icons/bs";
import Skeleton from "react-loading-skeleton";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import {
  useAddCardMutation,
  useGetInvoiceMutation,
  useGetPlanInfoQuery,
  usePostCancelSubscriptionMutation,
  usePutMakeCardAsDefaultMutation,
  useRemoveSubscriptionCardMutation
} from "../../../app/services/billing-rtk.service";
import { confirm } from "../../../components/common/confirmation/confirmation";
import LoadingComponent from "../../../components/common/loading/loading.component";
import ApiFailure from "../../../components/ui/ApiFailure";
import { AlertsType } from "../../../constants/alerts.constant";
import { MultiLanguageUrl } from "../../../constants/multiLanguage.constant";
import { ApsumoPlans, Plans } from "../../../constants/plans.constant";
import {
  ApiPlan,
  EPlanCodes,
  getHighestValidPlan,
  planLevelsArray
} from "./billing-create";

type CardType = {
  brand: string;
  country: string;
  description: any;
  expMonth: number;
  expYear: number;
  last4: string;
  isDefault: boolean;
  paymentMethodId: string;
};

type HistoryType = {
  paymentDateTime: string;
  paymentNextDate: string;
  isSuccess: boolean;
  name: string;
  paymentDetailMap: { invoice_id: string };
};

type KnowledgeBasesType = {
  name: string;
  myPlans: string;
  timePeriod: string;
  remainingDays: string;
  expireDate: string;
  defaultSubscribtionCard?: CardType[];
  histories?: HistoryType[];
  isCancelThisPlan: boolean;
  isPurchaseHistory: boolean;
  isPlanAllowedToCancel: boolean;
  currentActiveCardId: string;
};

const CardComponent = ({ planItem }: { planItem: KnowledgeBasesType }) => {
  const { i18n, t } = useTranslation();
  const [show, setShow] = useState(false);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const card = planItem?.defaultSubscribtionCard;

  const [data, setData] = useState<any[] | null>(null);
  const [putMakeCardAsDefault, putMakeCardAsDefaultStatus] =
    usePutMakeCardAsDefaultMutation();
  const [RemoveSubscriptionCard, RemoveSubscriptionCardStatus] =
    useRemoveSubscriptionCardMutation();

  const [AddCard, AddCardStatus] = useAddCardMutation();

  const onChange = (select: any) => {
    const value = select;
    putMakeCardAsDefault({ plan: planItem.name, payment_method_id: value })
      .unwrap()
      .then((res) => {
        toast.success(res.element.details || res.message);
      })
      .catch((res) => {
        if (res.data && res.status == 400) {
          toast.error(res.data.message);
        }
      });
  };

  const showEditCard = (item: KnowledgeBasesType) => {
    handleShow();
  };

  const addNewCard = (item: KnowledgeBasesType) => {
    AddCard({
      plan: item.name,
      redirectUrl: `${
        MultiLanguageUrl[i18n.language] || "en-US"
      }/control-panel/billing`
    })
      .unwrap()
      .then((res) => {
        if (res?.redirect) {
          window.open(res.redirect, "_self");
        }
      })
      .catch((res) => {
        if (res.data && res.status == 400) {
          toast.error(res.data.message);
        }
      });
  };

  const deleteCard = (
    last4: string,
    paymentCardId: string,
    planCardsLength: number
  ) => {
    confirm(`${t(AlertsType.ISPMKO002)} ${last4} ?`).then((res) => {
      if (res) {
        RemoveSubscriptionCard({
          plan: planItem.name,
          payment_method_id: paymentCardId,
          planCardsLength: planCardsLength
        })
          .unwrap()
          .then((res) => {
            toast.success(t(AlertsType.ISPMKO003));
          })
          .catch((res) => {
            if (res.data && res.status == 400) {
              toast.error(res.data.message);
            }
          });
      }
    });
  };

  return (
    <div className="fs-5 fw-normal form-label p-2 pt-3 dashboard__billing  col-12">
      <div className="d-flex">
        <Dropdown>
          <Dropdown.Toggle
            variant=""
            id="plansList-dropdown"
            size="sm"
            className=" rounded-0 rounded-start bg-twentyTwoColor color-fifthColor border-end"
          >
            {planItem?.defaultSubscribtionCard?.find(
              (item) => item.paymentMethodId === planItem.currentActiveCardId
            )?.last4
              ? "**** **** **** " +
                planItem?.defaultSubscribtionCard?.find(
                  (item) =>
                    item.paymentMethodId === planItem.currentActiveCardId
                )?.last4
              : t("General/key1")}
          </Dropdown.Toggle>

          <Dropdown.Menu>
            {planItem?.defaultSubscribtionCard?.map((dr, index) => (
              // eslint-disable-next-line react/jsx-key
              <Dropdown.Item
                as="span"
                key={index}
                onClick={() => onChange(dr.paymentMethodId)}
                className="cursor-pointer"
              >
                **** **** **** {dr.last4} &nbsp;&nbsp;&nbsp; &nbsp;&nbsp;
                {planItem.currentActiveCardId === dr.paymentMethodId && "✓"}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>

        <Button
          variant="info"
          className="d-flex align-items-center tw-rounded-s-none"
          size="sm"
          onClick={() => {
            showEditCard(planItem);
          }}
        >
          {putMakeCardAsDefaultStatus.isLoading && (
            <div className="me-2 p-1 pt-2">
              <Spinner animation="border" role="status" size="sm">
                <span className="visually-hidden ">
                  {t("CardComponent/text-01")}
                </span>
              </Spinner>
            </div>
          )}
          {t("CardComponent/btn-01")}
        </Button>
      </div>

      {show && (
        <Modal show={show} onHide={handleClose}>
          <Modal.Header closeButton>
            <Modal.Title>
              <h3 className="font-poppins">{t("CardComponent/t-01")}</h3>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Container>
              <Row>
                <Col>
                  {!planItem.defaultSubscribtionCard && (
                    <div className="d-flex justify-content-center">
                      <Spinner animation="border" role="status">
                        <span className="visually-hidden">
                          {t("CardComponent/text-01")}
                        </span>
                      </Spinner>
                    </div>
                  )}
                  {planItem.defaultSubscribtionCard &&
                    planItem.defaultSubscribtionCard.map((dr, index) => {
                      return (
                        <Row key={index}>
                          <Col className="align-items-center d-flex justify-content-between p-2">
                            <div> **** **** **** {dr.last4}</div>
                            <div>
                              {planItem.defaultSubscribtionCard &&
                                planItem.defaultSubscribtionCard.length > 1 &&
                                !dr.isDefault && (
                                  <BiTrash
                                    style={{ cursor: "pointer" }}
                                    onClick={() => {
                                      deleteCard(
                                        dr.last4,
                                        dr.paymentMethodId,
                                        planItem.defaultSubscribtionCard
                                          ?.length ?? 0
                                      );
                                    }}
                                  />
                                )}
                            </div>
                          </Col>
                        </Row>
                      );
                    })}
                </Col>
              </Row>
            </Container>
          </Modal.Body>
          <Modal.Footer>
            <Button
              size="sm"
              variant="primary"
              className="w-100 d-flex  justify-content-center"
              onClick={() => {
                addNewCard(planItem);
              }}
            >
              {t("CardComponent/btn-02")}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

const BillingPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [globalFilter, setGlobalFilter] = useState("");
  const [showPurchaseHistory, setShowPurchaseHistory] = useState(false);
  const [purchaseHistoryData, setPurchaseHistoryData] = useState<HistoryType[]>(
    []
  );
  const [data, setData] = useState<KnowledgeBasesType[]>([]);

  const getPlanInfo = useGetPlanInfoQuery();

  const handleClosePurchaseHistory = () => setShowPurchaseHistory(false);
  const handleShowPurchaseHistory = () => setShowPurchaseHistory(true);

  const [CancelSubscription, CancelSubscriptionStatus] =
    usePostCancelSubscriptionMutation();

  const [GetInvoiceMutation, GetInvoiceMutationStatus] =
    useGetInvoiceMutation();

  const cancelPlan = (item: KnowledgeBasesType) => {
    confirm(
      `${t(AlertsType.ISPMKO004)} ${planCodesMapper[item.name]
        .split("_")[0]
        .toLocaleLowerCase()} ${item.timePeriod.toLocaleLowerCase() ?? ""} ?`
    ).then((res) => {
      if (res) {
        CancelSubscription({ plan: item.name })
          .unwrap()
          .catch((res) => {
            if (res.data && res.status == 400) {
              toast.error(res.data.message);
            }
          });
      }
    });
  };

  const displayPurchaseHistory = (item: KnowledgeBasesType) => {
    if (item.histories) {
      setPurchaseHistoryData(item.histories);
      setShowPurchaseHistory(true);
    }
  };

  const planCodesMapper: Record<string, string> = {
    CHATBOT_FREE: "FREE",
    CHABOTC_LSH3RKBR5W: "FREE_EU",
    CHATBOT_BASIC_MONTHLY: "BASIC_MONTHLY",
    CHABOM_YXN1LILRWY: "BASIC_MONTHLY_EU",
    CHATBOT_BASIC_ANNUAL: "BASIC_ANNUAL",
    CHABOA_02OW2E8C4U: "BASIC_ANNUAL_EU",
    CHATBOT_BASIC_ANNUAL_TRIAL: "BASIC_ANNUAL_TRIAL",
    CHATBOT_ADVANCED_MONTHLY: "ADVANCED_MONTHLY",
    CHABOM_OSMFCYDT0V: "ADVANCED_MONTHLY_EU",
    CHATBOT_ADVANCED_ANNUAL: "ADVANCED_ANNUAL",
    CHABOA_B42CDBJP56: "ADVANCED_ANNUAL_EU",
    CHATBOT_ADVANCED_ANNUAL_TRIAL: "ADVANCED_ANNUAL_TRIAL",
    CHABOA_Y8O3FH61GW: "ENTERPRISE_ANNUAL_EU",
    CHABOM_5Q5FNDRELE: "ENTERPRISE_ANNUAL",
    CHABOM_TX5B3V1J9V: "ENTERPRISE_MONTHLY_EU",
    CHABOM_8F83AHUURU: "ENTERPRISE_MONTHLY",
    LB_APPSUMO_TIER1: "APPSUMO_TIER_1",
    LB_APPSUMO_TIER2: "APPSUMO_TIER_2",
    LB_APPSUMO_TIER3: "APPSUMO_TIER_3 ",
    UNLOCK_ALL_WELCOME: "WELCOME"
  };

  const columnHelper = createColumnHelper<KnowledgeBasesType>();

  const columns = [
    columnHelper.accessor("myPlans", {
      header: () => (
        <span className="fs-6 fw-semibold form-label tw-text-[#687076]">
          {t("BillingPage/t-01")}
        </span>
      ),
      cell: (info) => {
        return (
          <div className="fs-5 fw-bold   col-lg-12 col-4 p-lg-0 p-3">
            {info.getValue()}
          </div>
        );
      },
      enableGlobalFilter: true
    }),
    columnHelper.accessor("timePeriod", {
      header: () => (
        <span className="fs-6 fw-semibold form-label tw-text-[#687076]">
          {t("BillingPage/t-02")}
        </span>
      ),
      cell: (info) => {
        return (
          <div className="fs-5 fw-bold col-lg-12 col-4 p-lg-0 p-3">
            {info.getValue()}
          </div>
        );
      },
      enableGlobalFilter: true
    }),
    columnHelper.accessor("expireDate", {
      header: () => (
        <span className="fs-6 fw-semibold form-label tw-text-[#687076]">
          {t("BillingPage/t-03")}
        </span>
      ),
      cell: (info) => {
        return (
          <div className="fs-5 fw-normal form-label col-lg-12 col-4 m-0 p-lg-0 p-3">
            {info.getValue()}
          </div>
        );
      },
      enableGlobalFilter: true
    }),
    columnHelper.accessor("remainingDays", {
      header: () => (
        <span className="fs-6 fw-semibold form-label tw-text-[#687076]">
          {t("BillingPage/t-04")}
        </span>
      ),
      cell: (info) => {
        return (
          <div className="fs-5 fw-normal form-label col-lg-12 col-4 m-0 p-lg-0 p-3 d-none d-lg-block">
            {info.getValue()}
          </div>
        );
      },
      enableGlobalFilter: true
    }),
    columnHelper.accessor("defaultSubscribtionCard", {
      header: () => (
        <span className="fs-6 fw-semibold form-label tw-text-[#687076]">
          {t("BillingPage/t-05")}
        </span>
      ),
      cell: (info) => {
        if (info.row.original.defaultSubscribtionCard) {
          return <CardComponent planItem={info.row.original} />;
        } else {
          return <div></div>;
        }
      },
      enableGlobalFilter: true
    }),
    columnHelper.accessor("isCancelThisPlan", {
      header: () => "",
      cell: (info) => (
        <div className="fs-5 fw-normal form-label p-2 pt-3 d-flex align-items-center gap-3 justify-content-center col-12 ">
          <div className="d-flex gap-2 w-100 justify-content-between">
            {info.row.original.isPurchaseHistory && (
              <Button
                variant="info"
                size="sm"
                onClick={() => {
                  displayPurchaseHistory(info.row.original);
                }}
              >
                {t("BillingPage/btn-01")}
              </Button>
            )}
            {
              info.getValue() && (
                // (info.row.original.isPlanAllowedToCancel ? (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => {
                    cancelPlan(info.row.original);
                  }}
                  // disabled={!info.row.original.isPlanAllowedToCancel}
                >
                  {t("BillingPage/btn-02")}
                </Button>
              )
              // ) : (
              //   <MyOverlayTrigger title={t("BillingPage/pop-01")} width={200}>
              //     <span
              //       className={`btn btn-danger btn-sm ${
              //         !info.row.original.isPlanAllowedToCancel ? "disabled" : ""
              //       }`}
              //       onClick={() => {
              //         cancelPlan(info.row.original);
              //       }}
              //     >
              //       {t("BillingPage/btn-02")}
              //     </span>
              //   </MyOverlayTrigger>
              // ))
            }
          </div>
        </div>
      )
    })
  ];

  const table = useReactTable({
    data: data,
    columns,
    state: {
      globalFilter
    },
    getCoreRowModel: getCoreRowModel(),
    onGlobalFilterChange: setGlobalFilter,
    getFilteredRowModel: getFilteredRowModel()
  });

  const handleBillingCreate = () => {
    if (getPlanInfo.data) {
      if (
        getPlanInfo.data.some((dr: any) =>
          ApsumoPlans.some((x) => x.name == dr.name)
        )
      ) {
        window.open("https://appsumo.com/products/louisebot");
        return;
      }
    }

    navigate("../billing-create");
  };

  const renderUpgradePlanBtn = () => {
    if (getPlanInfo.data) {
      const highestPlan = getHighestValidPlan(
        getPlanInfo.data,
        planLevelsArray
      ) || {
        name: EPlanCodes.FREE,
        level: 1
      };

      if (
        [
          EPlanCodes.ENTERPRISE_ANNUAL,
          EPlanCodes.ENTERPRISE_ANNUAL_EU
        ].includes(highestPlan.name as EPlanCodes)
      )
        return null;

      return (
        <Col className="d-flex justify-content-end align-items-center">
          <Button
            size="sm"
            onClick={handleBillingCreate}
            variant="info"
            className="d-flex gap-2 align-items-center"
          >
            <BsPlusLg className="fs-5 fw-bold" />
            <span className="fw-bold">{t("BillingPage/btn-03")} </span>
          </Button>
        </Col>
      );
    }
  };

  useEffect(() => {
    if (getPlanInfo.isError) {
      if (getPlanInfo?.error) {
        if ((getPlanInfo.error as any)?.status == 400) {
          toast.error((getPlanInfo.error as any)?.data?.message);
        }
      }
    }
  }, [getPlanInfo.isError]);

  const downloadInVoice = (invoceId: string) => {
    GetInvoiceMutation({
      invoice_id: invoceId
    }).then((res: any) => {
      if (res?.data?.invoicePDFLink) {
        window.open(res?.data?.invoicePDFLink, "_blank", "noopener,noreferrer");
      }
    });
  };

  useEffect(() => {
    if (
      getPlanInfo.isSuccess &&
      getPlanInfo.data &&
      getPlanInfo.data.length > 0
    ) {
      let plansData = getPlanInfo.data.filter(
        (plan: ApiPlan) => plan.type === "SUBSCRIPTION"
      );

      if (
        plansData.some(
          (dr: any) =>
            ApsumoPlans.some((x) => x.name == dr.name) &&
            plansData.some((x: any) =>
              x.histories.some((z: any) => z.paymentStatusEnum == "SUCCEEDED")
            )
        )
      ) {
        const data = plansData.filter((dr: any) =>
          dr.histories.some((x: any) => x.paymentStatusEnum == "SUCCEEDED")
        ) as any[];

        if (data.length > 1) {
          plansData = data.filter((dr) =>
            ApsumoPlans.some((x) => x.name == dr.name)
          );
        } else {
          plansData = data;
        }
      }

      const tempData = plansData.map((dr: any) => {
        const getMyPlans = (item: any): string => {
          // const data = Plans.find((dr) => dr.name == item.name);
          // if (data) {
          //   return t(data.name);
          // } else if (
          //   ApsumoPlans.some(
          //     (dr) =>
          //       (dr.name as string).toUpperCase() == item.name.toUpperCase()
          //   )
          // ) {
          //   const plan = ApsumoPlans.find(
          //     (dr) =>
          //       (dr.name as string).toUpperCase() == item.name.toUpperCase()
          //   );

          //   if (plan) {
          //     return plan.title;
          //   } else {
          //     return item.name;
          //   }
          // }

          return planCodesMapper[item.name].split("_")[0];
        };

        const getTimePeriod = (item: any): string => {
          // const data = Plans.find((dr) => dr.name == item.name);
          // if (data) {
          //   if (data.title == "PlansConstant/t-02") {
          //     return "";
          //   } else {
          //     return data?.isMonthly ? "Monthly" : "Annually";
          //   }
          // } else if (
          //   ApsumoPlans.some(
          //     (dr) =>
          //       (dr.name as string).toUpperCase() == item.name.toUpperCase()
          //   )
          // ) {
          //   return "";
          // }

          // Following logic most be modified with multiple currency

          const lastSegment = planCodesMapper[item.name].split("_").pop();

          return lastSegment === "MONTHLY"
            ? "Monthly"
            : lastSegment === "ANNUAL"
            ? "Annual"
            : "";
        };

        const getExpireDate = (item: any): string => {
          const data = Plans.find((dr) => dr.name == item.name);
          if (data) {
            if (data.name == "PlansConstant/t-02") {
              return "";
            }
          }
          const activePlan = item?.histories ? item?.histories[0] : false;
          if (activePlan)
            if (activePlan?.paymentNextDate == null) {
              return "";
            }

          return moment(new Date(activePlan.paymentNextDate)).format(
            "MMMM Do YYYY"
          );
        };

        const getRemainingDays = (item: any): string => {
          const data = Plans.find((dr) => dr.name == item.name);
          if (data) {
            if (data.name == "PlansConstant/t-02") {
              return "";
            }
          }

          const activePlan = item.histories?.find(
            (x: any) =>
              x.paymentStatusEnum === "ACTIVE" ||
              x.paymentStatusEnum === "CANCELED"
          );
          if (
            activePlan &&
            moment(new Date(activePlan.paymentNextDate)) > moment()
          )
            return moment(new Date(activePlan.paymentNextDate)).fromNow();
          return "";
        };

        const getIsCancelThisPlan = (item: any): boolean => {
          const activePlan = item.histories?.some(
            (x: any) => x.paymentStatusEnum == "ACTIVE"
          );
          const data = Plans.find((dr) => dr.name == item.name);
          if (activePlan && data?.name !== "PlansConstant/t-02") {
            return true;
          }
          return false;
        };

        const getHistories = (item: any): HistoryType[] => {
          return item?.histories?.map((x: any) => {
            return {
              isSuccess:
                x.paymentStatusEnum == "ACTIVE" ||
                x.paymentStatusEnum == "SUCCEEDED"
                  ? true
                  : false,
              paymentDateTime: x.paymentDateTime,
              paymentNextDate: x.paymentNextDate,
              name: x.paymentStatusEnum,
              paymentDetailMap: x.paymentDetailMap
            } as HistoryType;
          });
        };

        const getIsPurchaseHistory = (item: any): boolean => {
          return true;
        };

        const getIsPlanAllowedToCancel = (item: any): boolean => {
          let result = true;
          item?.histories?.forEach((x: any) => {
            if (x.paymentStatusEnum == "ACTIVE") {
              let difference =
                new Date().getTime() - new Date(x.paymentDateTime).getTime();
              difference = Math.round(difference / 60000); // get Minutes
              difference = Math.round(difference / 60); // get Hours
              difference = Math.round(difference / 24); // get Days
              result = difference <= 7 ? false : result;
            }
          });
          return result;
        };

        return {
          name: dr.name,
          myPlans: getMyPlans(dr),
          expireDate: getExpireDate(dr),
          remainingDays: getRemainingDays(dr),
          timePeriod: getTimePeriod(dr),
          isCancelThisPlan: getIsCancelThisPlan(dr),
          isPurchaseHistory: getIsPurchaseHistory(dr),
          defaultSubscribtionCard: dr.cards?.map((item: any) => {
            let isDefault = false;

            if (dr?.details?.default_payment_method) {
              if (item.paymentMethodId === dr?.details?.default_payment_method)
                isDefault = true;
            }

            if ((dr.cards as any[]).length == 1) {
              isDefault = true;
            }

            return {
              isDefault: isDefault,
              brand: item.brand,
              country: item.country,
              expMonth: item.expMonth,
              expYear: item.expYear,
              last4: item.last4,
              paymentMethodId: item.paymentMethodId
            } as CardType;
          }),
          histories: getHistories(dr),
          isPlanAllowedToCancel: getIsPlanAllowedToCancel(dr),
          currentActiveCardId: dr?.details?.default_payment_method
        } as KnowledgeBasesType;
      });

      setData(tempData);
    }
  }, [getPlanInfo.isSuccess, getPlanInfo.data, t]);

  let content;

  if (getPlanInfo.isLoading) content = <LoadingComponent />;

  if (getPlanInfo.isError)
    content = <ApiFailure refetch={getPlanInfo.refetch} />;

  if (getPlanInfo.isSuccess)
    content = (
      <React.Fragment>
        <Row className="py-3  border-bottom align-items-center mx-0 bg-secondColor">
          <Col className="px-1">
            <h2 className="m-0 md:tw-px-5 tw-px-1">{t("BillingPage/t-06")}</h2>
          </Col>
          {renderUpgradePlanBtn()}
        </Row>

        <Row className="d-none d-lg-block mx-0">
          <Col className="p-0">
            <Table responsive hover>
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th key={header.id}>
                        {header.isPlaceholder ? null : (
                          <div className="tw-px-5">
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} style={{ verticalAlign: "middle" }}>
                        <div className="tw-px-5">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}

                {/* Rendering non subscription plans */}
                {getPlanInfo.data
                  .filter((plan: ApiPlan) => plan.type !== "SUBSCRIPTION")
                  .map((promoPlan: ApiPlan, index: number) => (
                    <tr key={index}>
                      <td style={{ verticalAlign: "middle" }}>
                        <div className="tw-px-5">
                          <div className="fs-5 fw-bold col-lg-12 col-4 p-lg-0 p-3">
                            Promo code:
                            <br />
                            {promoPlan.name}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="tw-px-5">
                          <div className="fs-5 fw-bold col-lg-12 col-4 p-lg-0 p-3">
                            {t("BillingPage/key1")}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="tw-px-5">
                          <div className="fs-5 fw-bold col-lg-12 col-4 p-lg-0 p-3">
                            {moment(
                              new Date(promoPlan.details?.end_date as string)
                            ).format("MMMM Do YYYY")}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="tw-px-5">
                          <div className="fs-5 fw-bold   col-lg-12 col-4 p-lg-0 p-3">
                            {moment(
                              new Date(promoPlan.details?.end_date as string)
                            ).fromNow()}
                          </div>
                        </div>
                      </td>
                      <td></td>
                      <td></td>
                    </tr>
                  ))}

                {getPlanInfo.isLoading && (
                  <tr>
                    <td>
                      <div className="fs-5 fw-bold p-2 pt-4">
                        <Skeleton height={30} />
                      </div>
                    </td>
                    <td>
                      <div className="fs-5 fw-bold p-2 pt-4">
                        <Skeleton height={30} />
                      </div>
                    </td>
                    <td>
                      <div className="fs-5 fw-bold p-2 pt-4">
                        <Skeleton height={30} />
                      </div>
                    </td>
                    <td>
                      <div className="fs-5 fw-bold p-2 pt-4">
                        <Skeleton height={30} />
                      </div>
                    </td>
                    <td>
                      <div className="fs-5 fw-bold p-2 pt-4">
                        <Skeleton height={30} />
                      </div>
                    </td>
                    <td>
                      <div className="fs-5 fw-bold p-2 pt-4">
                        <Skeleton height={30} />
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Col>
        </Row>

        <Row className="d-lg-none mx-0">
          <Col>
            {table.getHeaderGroups().map((headerGroup) => (
              <Row
                key={headerGroup.id}
                className="bg-twentyTwoColor py-3  border-bottom"
              >
                {headerGroup.headers.splice(0, 3).map((header) => (
                  <Col md="4" sm="4" xs="4" key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </Col>
                ))}
              </Row>
            ))}

            {table.getRowModel().rows.map((row) => (
              <Row key={row.id} className=" border-bottom">
                {row.getVisibleCells().map((cell) => (
                  <React.Fragment key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </React.Fragment>
                ))}
              </Row>
            ))}

            {getPlanInfo.data
              .filter((plan: ApiPlan) => plan.type !== "SUBSCRIPTION")
              .map((promoPlan: ApiPlan, index: number) => (
                <Row key={index} className=" border-bottom">
                  <div className="fs-5 fw-bold   col-lg-12 col-4 p-lg-0 p-3 tw-break-words">
                    Promo code:
                    <br />
                    {promoPlan.name}
                  </div>
                  <div className="fs-5 fw-normal form-label col-lg-12 col-4 m-0 p-lg-0 p-3">
                    {""}
                  </div>
                  <div className="fs-5 fw-bold col-lg-12 col-4 p-lg-0 p-3">
                    {moment(
                      new Date(promoPlan.details?.end_date as string)
                    ).format("MMMM Do YYYY")}
                  </div>
                  <div className="fs-5 fw-normal form-label col-lg-12 col-4 m-0 p-lg-0 p-3 d-none d-lg-block"></div>
                </Row>
              ))}

            {getPlanInfo.isLoading && (
              <div className=" border-bottom row">
                <div className="fs-5 fw-bold   col-lg-12 col-4 p-lg-0 p-3">
                  <Skeleton height={30} />
                </div>
                <div className="fs-5 fw-bold  col-lg-12 col-4 p-lg-0 p-3">
                  <Skeleton height={30} />
                </div>
                <div className="fs-5 fw-normal form-label col-lg-12 col-4 m-0 p-lg-0 p-3">
                  <Skeleton height={30} />
                </div>
                <div className="fs-5 fw-normal form-label p-2  d-none d-lg-block m-0">
                  <Skeleton height={30} />
                </div>
              </div>
            )}
          </Col>
        </Row>

        <Modal
          show={showPurchaseHistory}
          onHide={handleClosePurchaseHistory}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <h3 className="font-poppins">{t("BillingPage/t-07")}</h3>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="pb-5">
            <Container>
              <Row>
                <Col>
                  <span className="form-label">{t("BillingPage/t-08")}</span>
                </Col>
              </Row>

              <Row className="mt-4">
                <Col>
                  {purchaseHistoryData.map((dr, index) => (
                    <div
                      key={index}
                      className="md:tw-flex-row tw-flex-col md:tw-gap-0 tw-gap-3 tw-flex md:tw-items-center tw-items-end "
                    >
                      <div className="tw-flex-row tw-flex  tw-w-full tw-gap-2 ">
                        <div>
                          {dr.isSuccess && (
                            <span className="rounded-circle p-3 bg-twentyTwoColor d-flex justify-content-center align-items-center">
                              <AiOutlineCheck className="color-twelveColor" />
                            </span>
                          )}
                          {!dr.isSuccess && (
                            <span className="rounded-circle p-3 bg-twentyTwoColor d-flex justify-content-center align-items-center">
                              <AiOutlineClose className="color-seventeenColor" />
                            </span>
                          )}
                        </div>
                        <div className="d-flex flex-column">
                          {dr.isSuccess && (
                            <label className="fs-5 fw-bold">
                              {dr.name?.toLocaleLowerCase()}
                            </label>
                          )}
                          {!dr.isSuccess && (
                            <label className="fs-5 fw-bold">
                              {dr.name?.toLocaleLowerCase()}
                            </label>
                          )}
                          <div className="tw-flex tw-flex-col  tw-gap-1  xl:tw-my-0 ">
                            <div className="d-flex gap-2 ">
                              <span className="tw-text-zinc-500 tw-text-xs tw-font-normal tw-font-['Sofia Sans'] tw-leading-[18px]">
                                {t("BillingPage/l-01")}
                              </span>
                              <span className="tw-text-zinc-500 tw-text-xs tw-font-bold tw-font-['Sofia Sans'] tw-leading-[18px]">
                                {dr.paymentDateTime}
                              </span>
                            </div>
                            <div className="d-flex gap-2">
                              <span className="tw-text-zinc-500 tw-text-xs tw-font-normal tw-font-['Sofia Sans'] tw-leading-[18px]">
                                {t("BillingPage/l-02")}
                              </span>
                              <span className="tw-text-zinc-500 tw-text-xs tw-font-bold tw-font-['Sofia Sans'] tw-leading-[18px]">
                                {dr.paymentNextDate}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {dr?.paymentDetailMap?.invoice_id && (
                        <button
                          disabled={GetInvoiceMutationStatus.isLoading}
                          type="button"
                          className="btn btn-info btn-sm tw-cursor-pointer mb-3"
                          onClick={() => {
                            downloadInVoice(dr?.paymentDetailMap?.invoice_id);
                          }}
                        >
                          {t("BillingPage/btn-06")}
                        </button>
                      )}
                    </div>
                  ))}
                </Col>
              </Row>
            </Container>
          </Modal.Body>
        </Modal>
      </React.Fragment>
    );

  return <section>{content}</section>;
};

export default BillingPage;
